/* eslint-disable react-hooks/exhaustive-deps */
import {
  ErrorMessage,
  Formik,
  type FormikProps,
  useFormikContext,
} from 'formik';
import { useEffect, useRef } from 'react';

import {
  DialogBody,
  DialogFooter,
  DialogSubClose,
  DialogSubContent,
  DialogSubHeader,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { Label } from '@src/design-system/label';
import { Input } from '@src/design-system/input';

import { CustomErrorMessage } from '@src/design-system/error-message';

import {
  appointmentFormNames,
  type TAppointmentFormValues,
} from '@shared/forms/appointment';
import {
  appointmentTemplateFormInitialValues,
  appointmentTemplateFormSchema,
  createInitialValueFromExistingAppointmentForm,
  type TAppointmentTemplateFormValues,
} from '@shared/forms/appointment-template';
import meetingTemplateApi from '@features/participants/services/meeting-template';
import { TiptapEditor } from '@src/shared/components/reusables/tiptap-editor';

const { interviewType, description } = appointmentFormNames;

const Content = ({
  closeRef,
  formik,
  subFormik,
}: {
  closeRef: React.RefObject<HTMLDivElement>;
  subFormik: FormikProps<TAppointmentTemplateFormValues>;
  formik: FormikProps<TAppointmentFormValues>;
}) => {
  useEffect(() => {
    subFormik.setFieldValue(description, formik.values.description);
  }, [formik.values.description]);

  useEffect(() => {
    subFormik.setFieldValue(interviewType, formik.values.interviewType);
  }, [formik.values.interviewType]);

  return (
    <DialogSubContent
      vertical='top'
      value='createTemplate'
    >
      <DialogSubHeader>Save This As Template</DialogSubHeader>
      <DialogBody>
        <div className='mb-6'>
          <Label htmlFor='templateName'>Template name</Label>
          <Input
            placeholder='Enter name for a template'
            onChange={(e) => subFormik.setFieldValue('name', e.target.value)}
            id='templateName'
          />

          <ErrorMessage
            name='name'
            component={CustomErrorMessage}
          />
        </div>

        <div>
          <Label htmlFor='templateDetail'>Appointment details</Label>
          <TiptapEditor
            key={formik.values.description}
            editable={false}
            contentClassName='bg-primary-30'
            value={formik.values.description}
          />
          <ErrorMessage
            name='description'
            component={CustomErrorMessage}
          />
        </div>
      </DialogBody>
      <DialogFooter>
        <Button
          onClick={() => {
            if (subFormik.errors.interviewType) {
              formik.setFieldError(
                interviewType,
                'Please select interview type before you create template'
              );
            }
            formik.setFieldTouched(interviewType, true, false);
            subFormik.submitForm();
          }}
          className='flex-1'
        >
          Save
        </Button>
      </DialogFooter>

      <DialogSubClose ref={closeRef} />
    </DialogSubContent>
  );
};

export function CreateAppointmentTemplateSubModal() {
  const formik = useFormikContext<TAppointmentFormValues>();

  const { mutate: createAppointmentTemplate } = meetingTemplateApi.useCreate();

  const closeRef = useRef<HTMLDivElement>(null);

  const handleSubmit = ({
    appointmentType,
    description,
    interviewType,
    name,
  }: TAppointmentTemplateFormValues) => {
    createAppointmentTemplate(
      {
        description,
        name,
        interview_type: interviewType,
        type: appointmentType,
      },
      {
        onSuccess: () => closeRef.current?.click(),
      }
    );
  };

  return (
    <Formik
      initialValues={
        createInitialValueFromExistingAppointmentForm(formik) ||
        appointmentTemplateFormInitialValues
      }
      validationSchema={appointmentTemplateFormSchema}
      onSubmit={handleSubmit}
    >
      {(subFormik) => (
        <Content
          closeRef={closeRef}
          formik={formik}
          subFormik={subFormik}
        />
      )}
    </Formik>
  );
}
